import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Avatar,
  Chip,
  Stack,
  Paper,
  Divider,
} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import PageLayout from '../../components/layout/PageLayout';
import Can from '../../components/permissions/Can';
import { useGetBrandByIdQuery } from '../../store/api';
import { toast } from 'react-toastify';
import { buildBrandRelationParams } from './brandApiHelpers';

const DetailItem = ({ icon, label, value }) => (
  <Grid item xs={12} sm={6}>
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
        bgcolor: 'background.default',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'primary.light',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {label}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mt: 0.5,
              fontWeight: 500,
              wordBreak: 'break-word',
            }}
          >
            {value ?? '—'}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  </Grid>
);

const ViewBrand = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data,
    isLoading: loading,
    isError,
    error,
  } = useGetBrandByIdQuery(
    { id, params: buildBrandRelationParams() },
    { skip: !id }
  );

  const b = data?.data ?? data ?? null;

  useEffect(() => {
    if (isError) {
      toast.error(error?.data?.message || 'Failed to load brand');
      navigate('/brands');
    }
  }, [isError, error, navigate]);

  if (loading || !b) {
    return (
      <PageLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={320}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  const paymentAccount =
    b?.payment_accounts?.[0]?.descriptor ||
    b?.payment_accounts?.[0]?.name ||
    b?.paymentAccounts?.[0]?.descriptor ||
    b?.paymentAccounts?.[0]?.name ||
    b?.brand_payment_accounts?.[0]?.payment_accounts?.account_name ||
    b?.brand_payment_accounts?.[0]?.payment_accounts?.name;

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Top Actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/brands')}
            variant="outlined"
          >
            Back
          </Button>

          <Can permission="brand-edit">
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/brands/edit/${id}`)}
            >
              Edit Brand
            </Button>
          </Can>
        </Box>

        {/* Main Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 5,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 4,
              background:
                'linear-gradient(135deg, rgba(25,118,210,0.08), rgba(25,118,210,0.02))',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              {/* Brand Avatar */}
              <Avatar
                variant="rounded"
                sx={{
                  width: 90,
                  height: 90,
                  borderRadius: 4,
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontSize: 34,
                  fontWeight: 700,
                }}
              >
                {b.name?.charAt(0)?.toUpperCase() || 'B'}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  {b.name ?? 'Brand'}
                </Typography>

                <Stack
                  direction="row"
                  spacing={1.5}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {b.type && (
                    <Chip
                      icon={<CheckCircleOutlineIcon />}
                      label={b.type}
                      color="primary"
                    />
                  )}

                  {(b.brandCategory?.name ||
                    b.brand_category?.name) && (
                      <Chip
                        icon={<CategoryOutlinedIcon />}
                        label={
                          b.brandCategory?.name ??
                          b.brand_category?.name
                        }
                        variant="outlined"
                      />
                    )}
                </Stack>
              </Box>
            </Stack>
          </Box>

          {/* Details */}
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontWeight: 600,
              }}
            >
              Brand Information
            </Typography>

            <Grid container spacing={2.5}>
              <DetailItem
                icon={<BrandingWatermarkIcon fontSize="small" />}
                label="Brand Name"
                value={b.name}
              />

              <DetailItem
                icon={<CategoryOutlinedIcon fontSize="small" />}
                label="Brand Type"
                value={b.type}
              />

              <DetailItem
                icon={<ReceiptLongOutlinedIcon fontSize="small" />}
                label="Invoice Limit"
                value={
                  b.invoice_limit != null
                    ? String(b.invoice_limit)
                    : '—'
                }
              />

              <DetailItem
                icon={<CreditCardOutlinedIcon fontSize="small" />}
                label="Payment Account"
                value={paymentAccount || '—'}
              />

              <DetailItem
                icon={<LocationOnOutlinedIcon fontSize="small" />}
                label="Address"
                value={b.address}
              />

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: 'primary.light',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <LanguageOutlinedIcon fontSize="small" />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        Website URL
                      </Typography>

                      <Divider sx={{ my: 1.5 }} />

                      <Typography
                        variant="body1"
                        sx={{
                          wordBreak: 'break-word',
                          lineHeight: 1.8,
                        }}
                      >
                        {b.url || '—'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewBrand;